import { FC, useEffect, useState } from 'react';
import {
  Grid, TextField, MenuItem, Button, Chip, IconButton, CircularProgress, Divider,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchBarcodeMappings, mapBarcode, generateSkuBarcode, deleteBarcodeMapping,
} from '../../store/barcodeStore/barcodeThunk';
import ConfirmDialog from '../Dialog/ConfirmDialog';

interface Props {
  isEditing: boolean;
  skuId?: string;
  // Controlled barcode type (SKU.barcodeMode) + scanned manufacturer code, owned by the form.
  mode: string;
  onModeChange: (mode: string) => void;
  manufacturerBarcode: string;
  onManufacturerBarcodeChange: (value: string) => void;
}

const MODE_OPTIONS = [
  { value: 'none', label: 'None (no barcode)' },
  { value: 'manufacturer', label: 'Manufacturer (scan printed code)' },
  { value: 'internal_sku', label: 'Internal — per item' },
  { value: 'internal_batch', label: 'Internal — per batch' },
];

// "manufacturer" -> "Manufacturer", "internal_batch" -> "Internal Batch".
const titleCase = (s: string) =>
  String(s || '').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// Full URL for the on-demand render endpoint (no auth — pure image).
const renderUrl = (code: string, symbology: string) => {
  const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/?$/, '/');
  return `${base}api/v1/erp/barcode/render?code=${encodeURIComponent(code)}&symbology=${symbology}`;
};

const SkuBarcodePanel: FC<Props> = ({ isEditing, skuId, mode, onModeChange, manufacturerBarcode, onManufacturerBarcodeChange }) => {
  const dispatch = useAppDispatch();
  const mappings = useAppSelector((state) => state.barcode.mappings);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; code: string } | null>(null);

  const loadMappings = () => {
    if (isEditing && skuId) dispatch(fetchBarcodeMappings(skuId));
  };
  useEffect(() => { loadMappings(); }, [isEditing, skuId]);

  const addManufacturer = async () => {
    if (!skuId || !manufacturerBarcode.trim()) return;
    setBusy(true);
    try {
      await dispatch(mapBarcode({ skuId, code: manufacturerBarcode.trim(), isPrimary: true })).unwrap();
      onManufacturerBarcodeChange('');
      loadMappings();
    } catch { /* toast handled in slice */ } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!skuId) return;
    setBusy(true);
    try {
      await dispatch(generateSkuBarcode({ skuId, isPrimary: true })).unwrap();
      loadMappings();
    } catch { /* toast handled in slice */ } finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await dispatch(deleteBarcodeMapping(id)).unwrap();
      loadMappings();
      setConfirmDelete(null);
    } catch { /* toast handled in slice */ } finally { setBusy(false); }
  };

  const activeMappings = (mappings || []).filter((m: any) => m.isActive);

  return (
    <>
      <Grid item xs={4} mt={1}>
        <h5 className='c-label2'>Barcode Type</h5>
        <TextField
          select
          className='c-text-field'
          variant='standard'
          value={mode || 'none'}
          onChange={(e) => onModeChange(e.target.value)}
          style={{ width: '100%' }}
          InputProps={{ disableUnderline: true, style: { fontSize: 14, height: 28 } }}
        >
          {MODE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      </Grid>

      {/* Manufacturer: record/scan the printed code */}
      {mode === 'manufacturer' ? (
        <Grid item xs={4} mt={1}>
          <h5 className='c-label2'>Record barcode</h5>
          <TextField
            className='c-text-field'
            placeholder='Scan or type the printed barcode'
            variant='standard'
            value={manufacturerBarcode}
            onChange={(e) => onManufacturerBarcodeChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && isEditing) { e.preventDefault(); addManufacturer(); } }}
            style={{ width: '100%' }}
            InputProps={{ disableUnderline: true, style: { fontSize: 14, height: 28 } }}
          />
          {isEditing ? (
            <Button startIcon={<AddIcon />} variant='outlined' size='small' sx={{ mt: 1 }} onClick={addManufacturer}
              disabled={busy || !manufacturerBarcode.trim()}>Add</Button>
          ) : (
            <span style={{ fontSize: 12, color: '#9da7b7' }}>Saved when you create the item. You can add more after.</span>
          )}
        </Grid>
      ) : null}

      {/* Internal per-item */}
      {mode === 'internal_sku' ? (
        <Grid item xs={8} mt={1}>
          <h5 className='c-label2'>Generated code</h5>
          <span style={{ fontSize: 13, color: '#9da7b7' }}>
            Generated from your account’s <strong>Barcode Format</strong> (Business → Settings).
            {isEditing ? ' Use “Generate” to (re)create the primary code.' : ' Created automatically when you save the item.'}
          </span>
          {isEditing ? (
            <div>
              <Button variant='outlined' size='small' sx={{ mt: 1 }} onClick={generate} disabled={busy}>
                {busy ? <CircularProgress size={16} /> : 'Generate code'}
              </Button>
            </div>
          ) : null}
        </Grid>
      ) : null}

      {/* Internal per-batch */}
      {mode === 'internal_batch' ? (
        <Grid item xs={8} mt={1}>
          <h5 className='c-label2'>Per-batch label</h5>
          <span style={{ fontSize: 13, color: '#9da7b7' }}>
            A separate label is generated for <strong>each batch</strong> at goods receipt (GRN), using your
            Barcode Format with the batch’s date / batch number. Nothing to set here.
          </span>
        </Grid>
      ) : null}

      {/* Existing codes (edit mode) */}
      {isEditing ? (
        <Grid item xs={12} mt={2}>
          <Divider sx={{ mb: 1 }} />
          <h5 className='c-label2'>Codes on this item</h5>
          {activeMappings.length === 0 ? (
            <span style={{ fontSize: 13, color: '#9da7b7' }}>No barcodes yet.</span>
          ) : (
            activeMappings.map((m: any) => (
              <div key={m._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', marginBottom: 6, background: '#f7f8fa', borderRadius: 8 }}>
                <img src={renderUrl(m.code, m.symbology)} alt={m.code} height={40}
                  style={{ background: '#fff', padding: 2, borderRadius: 4 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 14 }}>{m.code}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                    <Chip size='small' label={titleCase(m.type)} />
                    <Chip size='small' variant='outlined' label={m.symbology} />
                    {m.isPrimary ? <Chip size='small' color='success' label='Primary' /> : null}
                    {m.batchId ? <Chip size='small' color='info' label='Batch' /> : null}
                  </div>
                </div>
                <IconButton size='small' onClick={() => setConfirmDelete({ id: m._id, code: m.code })} disabled={busy}><DeleteOutlineIcon fontSize='small' /></IconButton>
              </div>
            ))
          )}
        </Grid>
      ) : null}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Barcode"
        subtitle="This action cannot be undone"
        message={<>Are you sure you want to delete barcode <b>{confirmDelete?.code}</b>?</>}
        warningText="This barcode will no longer resolve to this item when scanned."
        confirmLabel="Delete Barcode"
        loading={busy}
        onConfirm={() => confirmDelete && remove(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
};

export default SkuBarcodePanel;
